import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Plus, RefreshCw, Pencil, UserX, UserCheck, Mail, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Same sections as the admin sidebar, in sidebar order.
const SIDEBAR_SECTIONS = [
  'dashboard',
  'inventory',
  'deals',
  'documents',
  'operations',
  'leads',
  'service',
  'imagine',
  'outreach',
  'schedule',
  'user_management',
];

function accessibleSectionCount(user) {
  if (!user) return 0;
  if (user.is_bootstrap_admin || user.role_type === 'management') {
    return SIDEBAR_SECTIONS.length;
  }
  const have = new Set(user.sections || []);
  return SIDEBAR_SECTIONS.filter((key) => have.has(key)).length;
}

const emptyForm = () => ({
  name: '',
  email: '',
  role_type: 'sales_customer_service',
  custom_role_label: '',
  sections: [],
});

export default function AdminUsersPage() {
  const navigate = useNavigate();
  const { token, hasSection, user: currentUser } = useAuth();
  const authHeaders = useCallback(
    () => ({ Authorization: `Bearer ${token}` }),
    [token]
  );

  const [users, setUsers] = useState([]);
  const [meta, setMeta] = useState({ sections: [], roles: [] });
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!hasSection('user_management')) {
      toast.error("You don't have access to User Management");
      navigate('/admin');
    }
  }, [hasSection, navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const headers = authHeaders();
      const [usersRes, metaRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/users`, { headers }),
        axios.get(`${BACKEND_URL}/api/admin/auth/meta`, { headers }),
      ]);
      setUsers(usersRes.data || []);
      setMeta(metaRes.data || { sections: [], roles: [] });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    const role = meta.roles?.find((r) => r.key === 'sales_customer_service');
    setForm({
      ...emptyForm(),
      sections: role?.default_sections ? [...role.default_sections] : [],
    });
    setModalOpen(true);
  };

  const isMainAdmin = !!currentUser?.is_bootstrap_admin;
  const isProtectedMainAdmin = (u) => !!u?.is_bootstrap_admin;
  /** Non–main-admins cannot change the main admin; main admin's permissions stay locked in UI too */
  const canEditUser = (u) => {
    if (!u) return false;
    if (isProtectedMainAdmin(u) && !isMainAdmin) return false;
    if (isProtectedMainAdmin(u) && isMainAdmin) return true; // name only enforced server-side for perms
    return true;
  };
  const canDeactivateUser = (u) => {
    if (!u || u.id === currentUser?.id) return false;
    if (isProtectedMainAdmin(u)) return false;
    return true;
  };
  const canDeleteUser = (u) => {
    if (!isMainAdmin || !u) return false;
    if (u.id === currentUser?.id) return false;
    if (isProtectedMainAdmin(u)) return false;
    return true;
  };

  const openEdit = (u) => {
    if (!canEditUser(u)) {
      toast.error('You cannot modify the main admin account');
      return;
    }
    setEditing(u);
    setForm({
      name: u.name || '',
      email: u.email || '',
      role_type: u.role_type || 'custom',
      custom_role_label: u.custom_role_label || (u.role_type === 'custom' ? u.role_label : '') || '',
      sections: [...(u.sections || [])],
      is_active: u.is_active !== false,
    });
    setModalOpen(true);
  };

  const onRoleChange = (role_type) => {
    const role = meta.roles?.find((r) => r.key === role_type);
    setForm((prev) => ({
      ...prev,
      role_type,
      sections:
        role_type === 'custom'
          ? prev.sections
          : role?.default_sections
            ? [...role.default_sections]
            : prev.sections,
    }));
  };

  const toggleSection = (key) => {
    setForm((prev) => {
      const has = prev.sections.includes(key);
      return {
        ...prev,
        sections: has ? prev.sections.filter((s) => s !== key) : [...prev.sections, key],
      };
    });
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.email.trim()) {
      toast.error('Name and email are required');
      return;
    }
    if (form.role_type === 'custom' && !form.custom_role_label.trim()) {
      toast.error('Enter a custom role name');
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        const payload =
          isProtectedMainAdmin(editing)
            ? { name: form.name.trim() }
            : {
                name: form.name.trim(),
                role_type: form.role_type,
                custom_role_label: form.role_type === 'custom' ? form.custom_role_label.trim() : null,
                sections: form.sections,
                is_active: form.is_active,
              };
        await axios.patch(
          `${BACKEND_URL}/api/admin/users/${editing.id}`,
          payload,
          { headers: authHeaders() }
        );
        toast.success('User updated');
      } else {
        const res = await axios.post(
          `${BACKEND_URL}/api/admin/users`,
          {
            name: form.name.trim(),
            email: form.email.trim(),
            role_type: form.role_type,
            custom_role_label: form.role_type === 'custom' ? form.custom_role_label.trim() : null,
            sections: form.sections,
          },
          { headers: authHeaders() }
        );
        toast.success(res.data.message || 'Invite sent');
        if (res.data.email_error) {
          toast.message('User created but email failed — use Resend Invite');
        }
      }
      setModalOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save user');
    } finally {
      setSaving(false);
    }
  };

  const resendInvite = async (u) => {
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/admin/users/${u.id}/resend-invite`,
        {},
        { headers: authHeaders() }
      );
      toast.success(res.data.message || 'Invite resent');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to resend invite');
    }
  };

  const toggleActive = async (u) => {
    if (!canDeactivateUser(u)) {
      toast.error('This account cannot be deactivated');
      return;
    }
    try {
      await axios.patch(
        `${BACKEND_URL}/api/admin/users/${u.id}`,
        { is_active: !u.is_active },
        { headers: authHeaders() }
      );
      toast.success(u.is_active ? 'User deactivated' : 'User reactivated');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update user');
    }
  };

  const deleteUser = async (u) => {
    if (!canDeleteUser(u)) {
      toast.error('Only the main admin can delete users');
      return;
    }
    const ok = window.confirm(
      `Permanently delete ${u.name || u.email}? This cannot be undone. Prefer deactivation if they might return.`
    );
    if (!ok) return;
    try {
      const res = await axios.delete(`${BACKEND_URL}/api/admin/users/${u.id}`, {
        headers: authHeaders(),
      });
      toast.success(res.data.message || 'User deleted');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete user');
    }
  };

  const statusBadge = (u) => {
    if (!u.is_active || u.invite_status === 'disabled') {
      return <span className="text-xs px-2 py-0.5 bg-red-500/15 text-red-400">Disabled</span>;
    }
    if (u.invite_status === 'pending') {
      return <span className="text-xs px-2 py-0.5 bg-amber-500/15 text-amber-400">Pending invite</span>;
    }
    return <span className="text-xs px-2 py-0.5 bg-emerald-500/15 text-emerald-400">Active</span>;
  };

  return (
    <div className="p-4 sm:p-6 md:p-8" data-testid="admin-users-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-['Unbounded'] font-bold text-white flex items-center gap-3">
            <Users className="w-7 h-7 text-blue-400" />
            User Management
          </h1>
          <p className="text-sm font-['Manrope'] text-white/40 mt-1">
            Invite employees, assign roles, and control portal section access.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={load}
            className="px-4 py-2 border border-white/10 text-white/70 text-sm font-['Manrope'] hover:bg-white/5 flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
          <button
            type="button"
            onClick={openCreate}
            data-testid="add-user-btn"
            className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm font-['Manrope'] font-semibold flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add User
          </button>
        </div>
      </div>

      <div className="border border-white/10 bg-white/[0.02] overflow-x-auto">
        <p className="lg:hidden px-4 py-2 text-[11px] font-['Manrope'] text-white/40">Swipe sideways to see all columns</p>
        <table className="w-full min-w-[720px] text-left text-sm font-['Manrope']">
          <thead className="border-b border-white/10 text-white/40 text-xs uppercase tracking-wider">
            <tr>
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Email</th>
              <th className="px-4 py-3">Role</th>
              <th className="px-4 py-3">Sections</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {loading ? (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center text-white/40">
                  Loading users…
                </td>
              </tr>
            ) : users.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center text-white/40">
                  No users yet.
                </td>
              </tr>
            ) : (
              users.map((u) => (
                <tr key={u.id} className="hover:bg-white/[0.02]">
                  <td className="px-4 py-3 text-white">
                    {u.name}
                    {u.id === currentUser?.id && (
                      <span className="ml-2 text-[10px] text-blue-400">(you)</span>
                    )}
                    {u.is_bootstrap_admin && (
                      <span className="ml-2 text-[10px] text-amber-400/90">Main Admin</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-white/70">{u.email}</td>
                  <td className="px-4 py-3 text-white/70">{u.role_label}</td>
                  <td className="px-4 py-3 text-white/50 text-xs max-w-[200px]">
                    {accessibleSectionCount(u)} sections
                  </td>
                  <td className="px-4 py-3">{statusBadge(u)}</td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1">
                      {canEditUser(u) && (
                        <button
                          type="button"
                          title={
                            isProtectedMainAdmin(u)
                              ? 'Main admin (name only; permissions locked)'
                              : 'Edit'
                          }
                          onClick={() => openEdit(u)}
                          className="p-2 text-white/50 hover:text-white hover:bg-white/5"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                      )}
                      {u.invite_status === 'pending' && !isProtectedMainAdmin(u) && (
                        <button
                          type="button"
                          title="Resend invite"
                          onClick={() => resendInvite(u)}
                          className="p-2 text-white/50 hover:text-blue-400 hover:bg-white/5"
                        >
                          <Mail className="w-4 h-4" />
                        </button>
                      )}
                      {canDeactivateUser(u) && (
                        <button
                          type="button"
                          title={u.is_active ? 'Deactivate' : 'Reactivate'}
                          onClick={() => toggleActive(u)}
                          className="p-2 text-white/50 hover:text-amber-400 hover:bg-white/5"
                        >
                          {u.is_active ? <UserX className="w-4 h-4" /> : <UserCheck className="w-4 h-4" />}
                        </button>
                      )}
                      {canDeleteUser(u) && (
                        <button
                          type="button"
                          title="Delete permanently (main admin only)"
                          onClick={() => deleteUser(u)}
                          className="p-2 text-white/50 hover:text-red-400 hover:bg-white/5"
                          data-testid={`delete-user-${u.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {modalOpen && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 max-lg:p-0 bg-black/80 backdrop-blur-sm">
          <div className="bg-[#0a0a0a] border border-white/10 w-full max-w-lg max-h-[90vh] max-lg:max-h-none max-lg:h-[100dvh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-white/10">
              <h2 className="text-lg font-['Unbounded'] font-bold text-white">
                {editing ? 'Edit User' : 'Add User'}
              </h2>
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                className="inline-flex items-center justify-center min-h-11 min-w-11 text-white/40 hover:text-white text-xl"
              >
                &times;
              </button>
            </div>
            <div className="p-5 space-y-4">
              {editing && isProtectedMainAdmin(editing) && (
                <p className="text-xs font-['Manrope'] text-amber-400/90 border border-amber-500/20 bg-amber-500/5 px-3 py-2">
                  Position can only be changed by the main admin, and never on their own account.
                </p>
              )}
              <div>
                <label className="block text-xs text-white/60 mb-1.5 font-['Manrope']">Name *</label>
                <input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50"
                />
              </div>
              <div>
                <label className="block text-xs text-white/60 mb-1.5 font-['Manrope']">Email *</label>
                <input
                  type="email"
                  value={form.email}
                  disabled={!!editing}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50 disabled:opacity-50"
                />
              </div>
              <div>
                <label className="block text-xs text-white/60 mb-1.5 font-['Manrope']">Role *</label>
                <select
                  value={form.role_type}
                  onChange={(e) => onRoleChange(e.target.value)}
                  disabled={
                    (editing && isProtectedMainAdmin(editing))
                    || (editing && (!isMainAdmin || editing.id === currentUser?.id))
                  }
                  className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50 [&>option]:bg-gray-900 disabled:opacity-50"
                >
                  {(meta.roles || []).map((r) => (
                    <option key={r.key} value={r.key}>
                      {r.label}
                    </option>
                  ))}
                </select>
              </div>
              {form.role_type === 'custom' && !(editing && isProtectedMainAdmin(editing)) && (
                <div>
                  <label className="block text-xs text-white/60 mb-1.5 font-['Manrope']">
                    Custom role name *
                  </label>
                  <input
                    value={form.custom_role_label}
                    onChange={(e) => setForm({ ...form, custom_role_label: e.target.value })}
                    placeholder="e.g. Lot Manager"
                    className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50"
                  />
                </div>
              )}
              <div>
                <label className="block text-xs text-white/60 mb-2 font-['Manrope']">
                  Portal sections *
                </label>
                <div className="space-y-2 max-h-48 overflow-y-auto border border-white/10 p-3">
                  {(meta.sections || []).map((s) => (
                    <label
                      key={s.key}
                      className={`flex items-center gap-2 text-sm text-white/80 font-['Manrope'] ${
                        editing && isProtectedMainAdmin(editing) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={
                          Boolean(editing && isProtectedMainAdmin(editing))
                          || form.sections.includes(s.key)
                        }
                        onChange={() => toggleSection(s.key)}
                        disabled={editing && isProtectedMainAdmin(editing)}
                        className="accent-blue-500"
                      />
                      {s.label}
                    </label>
                  ))}
                </div>
              </div>
              {!editing && (
                <p className="text-xs text-white/40 font-['Manrope']">
                  An invite email will be sent so they can set their password.
                </p>
              )}
            </div>
            <div className="flex justify-end gap-3 p-5 border-t border-white/10">
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                className="px-4 py-2 border border-white/10 text-white/60 text-sm font-['Manrope']"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="px-5 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm font-['Manrope'] font-semibold disabled:opacity-50"
              >
                {saving ? 'Saving…' : editing ? 'Save Changes' : 'Create & Send Invite'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
